import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';

// No shop SOP exists for furniture (sofa/loveseat, etc.) yet — this form and
// calcFurniture() (server/src/plugins/sopCalculations.ts) exist purely to
// test the quote → work order flow end to end with a real, non-trivial build
// spec. Only Width/Depth/Height feed the (deliberately rough, best-guess)
// pricing formula — every other field here is a pure build measurement
// carried straight through to the Work Order traveler, no pricing effect.

// Slugs match NewBlueprintSession.tsx's FURNITURE_TYPES labels exactly (one
// of the 3 the Blueprint generator accepts) — stored so a Work Order's
// furniture line item can deep-link into a pre-filled Blueprint session
// (?draftQuoteObjectId=...) without guessing the type from free text.
const FURNITURE_STYLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'loveseat', label: 'Loveseat' },
  { value: 'sofa_couch', label: 'Sofa / Couch' },
  { value: 'sectional', label: 'Sectional / L-Couch' },
];

interface State {
  furnitureType: string;
  widthInches: string;
  depthInches: string;
  heightInches: string;
  seatHeightInches: string;
  seatDepthInches: string;
  armHeightInches: string;
  backHeightInches: string;
  legHeightInches: string;
  cushionThicknessInches: string;
  legProfile: string;
  armThicknessInches: string;
  seatDeckThicknessInches: string;
  backCushionThicknessInches: string;
  fabricDescription: string;
}

const FurnitureForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;

  const [state, setState] = useState<State>({
    furnitureType: iv?.furnitureType || '',
    widthInches: iv?.widthInches != null ? String(iv.widthInches) : '',
    depthInches: iv?.depthInches != null ? String(iv.depthInches) : '',
    heightInches: iv?.heightInches != null ? String(iv.heightInches) : '',
    seatHeightInches: iv?.seatHeightInches != null ? String(iv.seatHeightInches) : '',
    seatDepthInches: iv?.seatDepthInches != null ? String(iv.seatDepthInches) : '',
    armHeightInches: iv?.armHeightInches != null ? String(iv.armHeightInches) : '',
    backHeightInches: iv?.backHeightInches != null ? String(iv.backHeightInches) : '',
    legHeightInches: iv?.legHeightInches != null ? String(iv.legHeightInches) : '',
    cushionThicknessInches: iv?.cushionThicknessInches != null ? String(iv.cushionThicknessInches) : '',
    legProfile: iv?.legProfile || '',
    armThicknessInches: iv?.armThicknessInches != null ? String(iv.armThicknessInches) : '',
    seatDeckThicknessInches: iv?.seatDeckThicknessInches != null ? String(iv.seatDeckThicknessInches) : '',
    backCushionThicknessInches: iv?.backCushionThicknessInches != null ? String(iv.backCushionThicknessInches) : '',
    fabricDescription: iv?.fabricDescription || '',
  });
  const [error, setError] = useState<string | null>(null);
  const s = (patch: Partial<State>) => setState((prev) => ({ ...prev, ...patch }));

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  const num = (v: string) => (v ? parseFloat(v) : null);

  useImperativeHandle(ref, () => ({
    getValues: () => {
      const width = parseFloat(state.widthInches);
      const depth = parseFloat(state.depthInches);
      const height = parseFloat(state.heightInches);
      if (!width || width <= 0 || !depth || depth <= 0 || !height || height <= 0) {
        setError('Width, Depth, and Height must be greater than 0');
        return null;
      }
      setError(null);
      return {
        furnitureType: state.furnitureType || null,
        widthInches: width,
        depthInches: depth,
        heightInches: height,
        seatHeightInches: num(state.seatHeightInches),
        seatDepthInches: num(state.seatDepthInches),
        armHeightInches: num(state.armHeightInches),
        backHeightInches: num(state.backHeightInches),
        legHeightInches: num(state.legHeightInches),
        cushionThicknessInches: num(state.cushionThicknessInches),
        legProfile: state.legProfile.trim() || null,
        armThicknessInches: num(state.armThicknessInches),
        seatDeckThicknessInches: num(state.seatDeckThicknessInches),
        backCushionThicknessInches: num(state.backCushionThicknessInches),
        fabricDescription: state.fabricDescription.trim() || null,
      };
    },
  }));

  return (
    <div>
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#92400e' }}>
        No SOP exists yet for furniture pricing — the Fabric yardage and Fabrication labor below are rough, best-guess placeholders (see each line's "Show math" note after adding). Every other measurement here has no pricing effect; it's carried straight through to the Work Order traveler.
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Furniture Style <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <select className="input" value={state.furnitureType} onChange={(e) => s({ furnitureType: e.target.value })} disabled={props.disabled}>
            <option value="">Select...</option>
            {FURNITURE_STYLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Width (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.widthInches} onChange={(e) => s({ widthInches: e.target.value })} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Depth (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.depthInches} onChange={(e) => s({ depthInches: e.target.value })} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Height (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.heightInches} onChange={(e) => s({ heightInches: e.target.value })} disabled={props.disabled} />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Seat Height (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.seatHeightInches} onChange={(e) => s({ seatHeightInches: e.target.value })} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Seat Depth (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.seatDepthInches} onChange={(e) => s({ seatDepthInches: e.target.value })} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Arm Height (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.armHeightInches} onChange={(e) => s({ armHeightInches: e.target.value })} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Back Height (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.backHeightInches} onChange={(e) => s({ backHeightInches: e.target.value })} disabled={props.disabled} />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Leg Height (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.legHeightInches} onChange={(e) => s({ legHeightInches: e.target.value })} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Leg Profile</label>
          <input type="text" className="input" placeholder='e.g., 1x1in square pegs' value={state.legProfile} onChange={(e) => s({ legProfile: e.target.value })} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Cushion Thickness (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.cushionThicknessInches} onChange={(e) => s({ cushionThicknessInches: e.target.value })} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Back Cushion Thickness (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.backCushionThicknessInches} onChange={(e) => s({ backCushionThicknessInches: e.target.value })} disabled={props.disabled} />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Arm Thickness (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.armThicknessInches} onChange={(e) => s({ armThicknessInches: e.target.value })} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Seat Deck Thickness (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={state.seatDeckThicknessInches} onChange={(e) => s({ seatDeckThicknessInches: e.target.value })} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 2 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={state.fabricDescription}
            onChange={(name) => s({ fabricDescription: name })}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default FurnitureForm;
